import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Eye, Search, X, ZoomIn, ZoomOut, RotateCcw, FileSpreadsheet, CheckCircle, XCircle, ReceiptEuro } from 'lucide-react';
import * as XLSX from 'xlsx';
import '../../Style/Drreportview.css';
import { getDateString, ServiceProvider } from '../../Api/ServiceProvider';
import { toast } from 'react-toastify';
import permissionService from '../../Permission/Permission';
import { useHistoryList } from '../Breadcrum/HistoryProvider';
import Breadcrumbs from '../Breadcrum/BreadcrumbHistory';
import SecureImage from '../ImgService/loadImage';
import { uploadModalProps } from '../../Const/constant';
import DrReportService from '../../Api/Drreportservice';
import ReportService from '../../Api/ReportService';

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;
    return `${day}/${month}/${year}, ${hours}:${minutes} ${ampm}`;
};

// Returns today's date as YYYY-MM-DD for max attribute on date inputs
const getTodayString = () => new Date().toISOString().split('T')[0];

// Tab → API status value mapping
const TAB_STATUS = {
    dr_request: 'dr_request',
    dr_approved: 'dr_approved',
    dr_completed: 'dr_completed',
};

const TAB_LABELS = {
    dr_request: 'DR Request',
    dr_approved: 'DR Approved',
    dr_completed: 'DR Completed',
};

const TABS = ['dr_request', 'dr_approved', 'dr_completed'];

// Status display & CSS helpers
function getStatusDisplay(status) {
    if (!status) return 'NA';
    const n = status.toLowerCase();
    if (n === 'completed') return 'Completed';
    if (n === 'in-progress') return 'In Progress';
    if (n === 'pending') return 'Pending';
    if (n === 'dr_request') return 'DR Request';
    if (n === 'dr_approved') return 'DR Approved';
    if (n === 'dr_completed') return 'DR Completed';
    return status;
}
function getStatusClass(status) {
    const n = status?.toLowerCase();
    if (n === 'dr_request') return 'report-status dr-request';
    if (n === 'dr_approved') return 'report-status dr-approved';
    if (n === 'dr_completed') return 'report-status dr-completed';
    if (n === 'completed') return 'report-status complete';
    if (n === 'in-progress') return 'report-status in-progress';
    if (n === 'pending') return 'report-status pending';
    return 'report-status inactive';
}

// ─────────────────────────────────────────────
// PDI TABLE HEADERS
// ─────────────────────────────────────────────
const PDI_HEADERS = [
    { key: 'sn', label: 'SNO' },
    { key: 'pn', label: 'PART NO' },
    { key: 'desc', label: 'DESCRIPTION' },
    { key: 'sz', label: 'SIZE' },
    { key: 'qty', label: 'QTY' },
];
const PDI_SEARCH_FIELDS = ['pn', 'desc', 'sz'];

// ─────────────────────────────────────────────
// APPROVE MODAL
// ─────────────────────────────────────────────
const ApproveModal = ({ report, onConfirm, onCancel, loading }) => {
    const [remark, setRemark] = useState('');
    const [touched, setTouched] = useState(false);

    const isRemarkValid = remark.trim().length > 0;
    const showError = touched && !isRemarkValid;

    if (!report) return null;
    return (
        <div className="dr-modal-overlay" onClick={onCancel}>
            <div className="dr-modal" onClick={e => e.stopPropagation()}>
                <div className="dr-modal-header">
                    <CheckCircle size={20} className="dr-modal-icon approve" />
                    <h3>Approve DR Request</h3>
                    <button className="dr-modal-close" onClick={onCancel}><X size={18} /></button>
                </div>
                <div className="dr-modal-body">
                    <div className="dr-modal-info-grid">
                        <div><span className="dr-info-label">TPL NO</span><span className="dr-info-value">{report.tpl_no}</span></div>
                        <div><span className="dr-info-label">FAB NO</span><span className="dr-info-value">{report.fab_no}</span></div>
                        <div><span className="dr-info-label">PO NO</span><span className="dr-info-value">{report.po_no}</span></div>
                        <div><span className="dr-info-label">PDI STATUS</span><span className={getStatusClass(report.pdi_status)}>{getStatusDisplay(report.pdi_status)}</span></div>
                    </div>
                    <div className="dr-modal-field">
                        <label>
                            Remark <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>*</span>
                        </label>
                        <textarea
                            className={`dr-modal-textarea ${showError ? 'dr-input-error' : ''}`}
                            rows={3}
                            placeholder="Enter approval remark (required)..."
                            value={remark}
                            onChange={e => { setRemark(e.target.value); setTouched(true); }}
                            onBlur={() => setTouched(true)}
                        />
                        {showError && (
                            <span className="dr-error-message" style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                Remark is required
                            </span>
                        )}
                    </div>
                </div>
                <div className="dr-modal-footer">
                    <button className="dr-btn-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
                    <button
                        className="dr-btn-approve"
                        onClick={() => onConfirm(report._id, remark)}
                        disabled={loading || !isRemarkValid}
                    >
                        {loading ? <><span className="rv-spinner" /> Approving...</> : <><CheckCircle size={15} /> Approve</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// COMPLETE MODAL
// ─────────────────────────────────────────────
const CompleteModal = ({ report, onConfirm, onCancel, loading }) => {
    const [remark, setRemark] = useState('');
    const [touched, setTouched] = useState(false);

    const isRemarkValid = remark.trim().length > 0;
    const showError = touched && !isRemarkValid;

    if (!report) return null;
    return (
        <div className="dr-modal-overlay" onClick={onCancel}>
            <div className="dr-modal" onClick={e => e.stopPropagation()}>
                <div className="dr-modal-header">
                    <CheckCircle size={20} className="dr-modal-icon complete" />
                    <h3>Complete DR Approval</h3>
                    <button className="dr-modal-close" onClick={onCancel}><X size={18} /></button>
                </div>
                <div className="dr-modal-body">
                    <div className="dr-modal-info-grid">
                        <div><span className="dr-info-label">TPL NO</span><span className="dr-info-value">{report.tpl_no}</span></div>
                        <div><span className="dr-info-label">FAB NO</span><span className="dr-info-value">{report.fab_no}</span></div>
                        <div><span className="dr-info-label">PO NO</span><span className="dr-info-value">{report.po_no}</span></div>
                        <div><span className="dr-info-label">PDI STATUS</span><span className={getStatusClass(report.pdi_status)}>{getStatusDisplay(report.pdi_status)}</span></div>
                    </div>
                    <div className="dr-modal-field">
                        <label>
                            Remark <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>*</span>
                        </label>
                        <textarea
                            className={`dr-modal-textarea ${showError ? 'dr-input-error' : ''}`}
                            rows={3}
                            placeholder="Enter completion remark (required)..."
                            value={remark}
                            onChange={e => { setRemark(e.target.value); setTouched(true); }}
                            onBlur={() => setTouched(true)}
                        />
                        {showError && (
                            <span className="dr-error-message" style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                Remark is required
                            </span>
                        )}
                    </div>
                </div>
                <div className="dr-modal-footer">
                    <button className="dr-btn-cancel" onClick={onCancel} disabled={loading}>Cancel</button>
                    <button
                        className="dr-btn-complete"
                        onClick={() => onConfirm(report._id, remark)}
                        disabled={loading || !isRemarkValid}
                    >
                        {loading ? <><span className="rv-spinner" /> Completing...</> : <><CheckCircle size={15} /> Complete</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const DrReportView = () => {

    // ── Core state ───────────────────────────────
    const [reportsData, setReportsData] = useState({
        dr_request: { data: [], count: 0 },
        dr_approved: { data: [], count: 0 },
        dr_completed: { data: [], count: 0 },
    });
    const [activeTab, setActiveTab] = useState('dr_request');
    const [searchQuery, setSearchQuery] = useState('');
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [exportingAll, setExportingAll] = useState(false);
    const [canView, setCanView] = useState(false);
    const [canRqEdit, setCanRqEdit] = useState(false);
    const [canComEdit, setCanComEdit] = useState(false);

    // ── Filter state — staged (what user types, not yet applied) ──
    const [stagedStartDate, setStagedStartDate] = useState('');
    const [stagedEndDate, setStagedEndDate] = useState('');

    // ── Filter state — applied (drives the API call) ──
    const [appliedStartDate, setAppliedStartDate] = useState('');
    const [appliedEndDate, setAppliedEndDate] = useState('');

    // ── Pagination ───────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);

    // ── Detail view (PDI items inside a report) ──
    const [selectedReport, setSelectedReport] = useState(null);
    const [detailSearch, setDetailSearch] = useState('');
    const [detailPage, setDetailPage] = useState(1);
    const [detailPerPage, setDetailPerPage] = useState(25);

    // ── Action modals ────────────────────────────
    const [approveTarget, setApproveTarget] = useState(null);
    const [completeTarget, setCompleteTarget] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    // ── Image viewer ─────────────────────────────
    const [fullScreenViewer, setFullScreenViewer] = useState({
        isOpen: false, images: [], initialIndex: 0,
    });

    const tabType = {
        dr_request: 'DR Request',
        dr_approved: 'DR Approved',
        dr_completed: 'DR Completed'
    };

    const BASE_IMAGE_URL = ServiceProvider.assert.get;

    // ── Today string for date input max ──────────
    const todayStr = getTodayString();

    // ── Refs ─────────────────────────────────────
    const isInitializedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const lastFetchParamsRef = useRef({ tab: null, page: null, count: null, search: null, startDate: null, endDate: null, timestamp: null });

    const { addCustomPath, removeCustomPath } = useHistoryList();

    // ── Init ─────────────────────────────────────
    useEffect(() => {
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;
        const user = JSON.parse(localStorage.getItem('user'));
        setUserData(user);
        if (user) {
            const hasView = permissionService.hasPermission('dr-reports', 'view');
            const hasReqEdit = permissionService.hasPermission('approve_dr', 'edit');
            const hasComEdit = permissionService.hasPermission('complete_dr', 'edit');
            setCanView(hasView);
            setCanRqEdit(hasReqEdit);
            setCanComEdit(hasComEdit);
            if (!hasView) toast.error("You don't have permission to access this module", { autoClose: 5000, position: 'top-center' });
        }
    }, []);

    // ── Core fetch ───────────────────────────────
    const fetchReports = useCallback(async (tab, page, count, search = '', startDate = '', endDate = '') => {
        if (isFetchingRef.current) return;
        if (!userData || !canView) return;

        const paramsKey = `${tab}-${page}-${count}-${search}-${startDate}-${endDate}`;
        const lastKey = `${lastFetchParamsRef.current.tab}-${lastFetchParamsRef.current.page}-${lastFetchParamsRef.current.count}-${lastFetchParamsRef.current.search}-${lastFetchParamsRef.current.startDate}-${lastFetchParamsRef.current.endDate}`;
        const now = Date.now();
        if (paramsKey === lastKey && now - (lastFetchParamsRef.current.timestamp || 0) < 1000) return;

        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        isFetchingRef.current = true;
        setLoading(true);
        lastFetchParamsRef.current = { tab, page, count, search, startDate, endDate, timestamp: now };

        try {
            const response = await DrReportService.getDrReports(tab, page, count, search, startDate, endDate);
            if (response.success && response.data.length > 0) {
                setReportsData(prev => ({
                    ...prev,
                    [tab]: { data: response.data || [], count: response.count || 0 },
                }));
            } else {
                setReportsData(prev => ({ ...prev, [tab]: { data: [], count: 0 } }));
                toast.info(`No records found for ${tabType[tab] || tab}`, { autoClose: 3000 });
            }
        } catch (err) {
            if (err?.name === 'AbortError') return;
            toast.error(err?.message || 'Unable to fetch DR Reports', { autoClose: 3000 });
            setReportsData(prev => ({ ...prev, [tab]: { data: [], count: 0 } }));
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [userData, canView]);

    // ── Fetch trigger ────────────────────────────
    useEffect(() => {
        if (!canView || !userData) return;
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (searchQuery.length > 0 && searchQuery.length < 3) return;

        const doFetch = () => fetchReports(activeTab, currentPage, itemsPerPage, searchQuery, appliedStartDate, appliedEndDate);

        if (searchQuery.length >= 3) {
            searchTimeoutRef.current = setTimeout(doFetch, 500);
        } else {
            doFetch();
        }

        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [activeTab, currentPage, itemsPerPage, searchQuery, canView, userData, fetchReports, appliedStartDate, appliedEndDate]);

    // ── Reset lastFetch ref (force next call through) ──
    const resetLastFetch = () => {
        lastFetchParamsRef.current = { tab: null, page: null, count: null, search: null, startDate: null, endDate: null, timestamp: null };
    };

    // ── Tab change ───────────────────────────────
    const handleTabChange = (tab) => {
        if (tab === activeTab) return;
        // FIX: Also reset staged filters on tab change so UI is consistent
        setStagedStartDate('');
        setStagedEndDate('');
        setAppliedStartDate('');
        setAppliedEndDate('');
        resetLastFetch();
        setActiveTab(tab);
        setCurrentPage(1);
        setSearchQuery('');
        setSelectedReport(null);
    };

    // ── Filter apply / clear ─────────────────────
    const handleApplyFilters = () => {
        if (stagedStartDate && stagedEndDate && stagedStartDate > stagedEndDate) {
            toast.error('Start date cannot be after end date', { autoClose: 3000 });
            return;
        }
        resetLastFetch();
        setAppliedStartDate(stagedStartDate);
        setAppliedEndDate(stagedEndDate);
        setCurrentPage(1);
    };

    // FIX 2: Clear resets both staged and applied state so UI inputs clear immediately
    const handleClearFilters = () => {
        setStagedStartDate('');
        setStagedEndDate('');
        resetLastFetch();
        setAppliedStartDate('');
        setAppliedEndDate('');
        setCurrentPage(1);
    };

    // FIX 3 & 4: Separate staged vs applied checks
    const hasActiveFilters = !!(appliedStartDate || appliedEndDate);
    // FIX 4: Both dates must be present to enable Filter button
    const hasStagedFilters = !!(stagedStartDate && stagedEndDate);

    // ── Pagination helpers ───────────────────────
    const currentReports = useMemo(() => reportsData[activeTab]?.data || [], [reportsData, activeTab]);
    const currentCount = useMemo(() => reportsData[activeTab]?.count || 0, [reportsData, activeTab]);
    const mainTotalPages = useMemo(() => Math.ceil(currentCount / itemsPerPage) || 1, [currentCount, itemsPerPage]);

    const handleFirstPage = () => { if (currentPage !== 1) { resetLastFetch(); setCurrentPage(1); } };
    const handlePreviousPage = () => { if (currentPage > 1) { resetLastFetch(); setCurrentPage(p => p - 1); } };
    const handleNextPage = () => { if (currentPage < mainTotalPages) { resetLastFetch(); setCurrentPage(p => p + 1); } };
    const handleLastPage = () => { if (currentPage !== mainTotalPages) { resetLastFetch(); setCurrentPage(mainTotalPages); } };
    const handlePageSizeChange = (val) => { resetLastFetch(); setItemsPerPage(val); setCurrentPage(1); };

    // ── Detail view ──────────────────────────────
    const handleViewDetail = (report) => {
        setSelectedReport(report);
        setDetailSearch('');
        setDetailPage(1);
        addCustomPath(`DR Report - ${TAB_LABELS[activeTab]}`);
    };
    const handleBackToList = () => { setSelectedReport(null); removeCustomPath(); };
    const backNav = () => handleBackToList();

    const filteredDetailItems = useMemo(() => {
        const items = selectedReport?.pdi || [];
        if (!detailSearch) return items;
        const term = detailSearch.toLowerCase();
        return items.filter(item => PDI_SEARCH_FIELDS.some(f => item[f]?.toString().toLowerCase().includes(term)));
    }, [selectedReport, detailSearch]);

    const paginatedDetailItems = useMemo(() => {
        return filteredDetailItems.slice((detailPage - 1) * detailPerPage, detailPage * detailPerPage);
    }, [filteredDetailItems, detailPage, detailPerPage]);

    const detailTotalPages = useMemo(() => Math.ceil(filteredDetailItems.length / detailPerPage) || 1, [filteredDetailItems, detailPerPage]);

    // ── Image viewer ─────────────────────────────
    const handleViewImages = (item, imageType = 'images') => {
        const images = item[imageType] || [];
        if (images.length > 0) setFullScreenViewer({ isOpen: true, images, initialIndex: 0 });
    };
    const handleCloseViewer = () => setFullScreenViewer({ isOpen: false, images: [], initialIndex: 0 });

    const getImageUrl = (item, imageType) => {
        const images = item[imageType] || [];
        if (!images.length) return null;
        return `${BASE_IMAGE_URL}images/pdi_report/${images[0]}`;
    };

    // ── APPROVE action ───────────────────────────
    const handleApproveConfirm = async (reportId, remark) => {
        setActionLoading(true);
        try {
            const response = await ReportService.update(
                reportId,
                { reportUpdate: { apr_remark: remark, pdi_status: 'dr_approved' } },
            );
            if (response.success) {
                toast.success('DR Request approved successfully', { autoClose: 3000 });
                setApproveTarget(null);
                resetLastFetch();
                handleBackToList();
                fetchReports(activeTab, currentPage, itemsPerPage, searchQuery, appliedStartDate, appliedEndDate);
            } else {
                toast.error(response.message || 'Approval failed', { autoClose: 3000 });
            }
        } catch (err) {
            toast.error(err?.message || 'Approval failed', { autoClose: 3000 });
        } finally {
            setActionLoading(false);
        }
    };

    // ── COMPLETE action ──────────────────────────
    const handleCompleteConfirm = async (api,remark) => {
        setActionLoading(true);

        try {
            const payload = {
                report: {
                    _id: selectedReport._id,
                    com_remark: remark,
                    pdi: selectedReport.pdi.map(item => ({
                        id: item.id
                    }))
                }
            };

            const response = await ReportService.drComplete(payload);

            if (response.success) {
                toast.success('DR Approved record marked as complete', { autoClose: 3000 });
                setCompleteTarget(null);
                resetLastFetch();
                handleBackToList();
                fetchReports(
                    activeTab,
                    currentPage,
                    itemsPerPage,
                    searchQuery,
                    appliedStartDate,
                    appliedEndDate
                );
            } else {
                toast.error(response.message || 'Complete action failed', { autoClose: 3000 });
            }
        } catch (err) {
            toast.error(err?.message || 'Complete action failed', { autoClose: 3000 });
        } finally {
            setActionLoading(false);
        }
    };

    // ── Export all to Excel ──────────────────────
    const handleExportAllToExcel = async () => {
        if (!canView) { toast.error('No permission to export'); return; }
        setExportingAll(true);
        try {
            const response = await DrReportService.getDrReports(activeTab, null, null, searchQuery, appliedStartDate, appliedEndDate);
            if (!response.success || !response.data?.length) {
                toast.warn('No data to export', { autoClose: 3000 });
                return;
            }
            const rows = response.data;
            const headers = ['NO.', 'TPL NO', 'DATE CREATED', 'PO NO', 'FAB NO', 'MODIFIED DATE',
                `${uploadModalProps['pdi'] || 'PDI'} STATUS`,'REMARK'
            ];
            const wsData = [headers, ...rows.map((r, idx) => [
                idx + 1,
                r.tpl_no || '',
                r.cd ? formatDateTime(r.cd) : 'N/A',
                r.po_no || '',
                r.fab_no || '',
                r.md ? formatDateTime(r.md) : 'N/A',
                getStatusDisplay(r.pdi_status),
                r.pdi_remark || ''
            ])];
            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet(wsData);
            ws['!cols'] = [{ wch: 6 }, { wch: 14 }, { wch: 20 }, { wch: 14 }, { wch: 14 }, { wch: 20 }, { wch: 18 }];
            XLSX.utils.book_append_sheet(wb, ws, 'DR Reports');
            const tab = TAB_LABELS[activeTab].replace(/\s+/g, '_');
            const today = getDateString(new Date());
            const suffix = [
                appliedStartDate ? `From_${appliedStartDate}` : '',
                appliedEndDate ? `To_${appliedEndDate}` : '',
            ].filter(Boolean).join('_');
            XLSX.writeFile(wb, `DR_Reports_${tab}${suffix ? '_' + suffix : ''}_${today}.xlsx`);
            toast.success(`Exported ${rows.length} records successfully`, { autoClose: 3000 });
        } catch (err) {
            console.error('Export error:', err);
            toast.error('Failed to export data', { autoClose: 3000 });
        } finally {
            setExportingAll(false);
        }
    };

    // ─────────────────────────────────────────────
    // FULLSCREEN IMAGE VIEWER
    // ─────────────────────────────────────────────
    const FullScreenImageViewer = ({ isOpen, onClose, images }) => {
        const [currentIndex, setCurrentIndex] = useState(0);
        const [scale, setScale] = useState(1);
        const [position, setPosition] = useState({ x: 0, y: 0 });
        const [isDragging, setIsDragging] = useState(false);
        const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
        const lastTouchDistRef = useRef(null);
        const ZOOM_STEP = 0.25; const MIN_SCALE = 1; const MAX_SCALE = 5;

        useEffect(() => { setCurrentIndex(0); setScale(1); setPosition({ x: 0, y: 0 }); }, [isOpen]);
        useEffect(() => { setScale(1); setPosition({ x: 0, y: 0 }); }, [currentIndex]);

        const zoomIn = (e) => { e?.stopPropagation(); setScale(p => Math.min(p + ZOOM_STEP, MAX_SCALE)); };
        const zoomOut = (e) => { e?.stopPropagation(); setScale(p => { const n = p - ZOOM_STEP; if (n <= MIN_SCALE) { setPosition({ x: 0, y: 0 }); return MIN_SCALE; } return n; }); };
        const resetZoom = () => { setScale(1); setPosition({ x: 0, y: 0 }); };

        const handleWheel = (e) => { e.preventDefault(); const d = e.deltaY < 0 ? 0.15 : -0.15; setScale(p => { const n = p + d; if (n <= MIN_SCALE) { setPosition({ x: 0, y: 0 }); return MIN_SCALE; } return Math.min(n, MAX_SCALE); }); };
        const handleMouseDown = (e) => { if (scale > 1) { e.preventDefault(); setIsDragging(true); setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y }); } };
        const handleMouseMove = (e) => { if (isDragging && scale > 1) setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }); };
        const handleMouseUp = () => setIsDragging(false);

        if (!isOpen || !images.length) return null;
        return (
            <div className="fullscreen-image-viewer" onClick={onClose}
                style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.86)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <div onClick={e => e.stopPropagation()}
                    style={{ position: 'absolute', top: 0, left: 0, right: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'linear-gradient(to bottom,rgba(0,0,0,0.7),transparent)', zIndex: 10 }}>
                    <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{currentIndex + 1} / {images.length}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {[
                            { action: zoomOut, icon: <ZoomOut size={16} />, disabled: scale <= MIN_SCALE, title: 'Zoom Out' },
                            { action: zoomIn, icon: <ZoomIn size={16} />, disabled: scale >= MAX_SCALE, title: 'Zoom In' },
                            { action: (e) => { e.stopPropagation(); resetZoom(); }, icon: <RotateCcw size={15} />, disabled: scale === 1, title: 'Reset' },
                        ].map((btn, i) => (
                            <button key={i} onClick={btn.action} disabled={btn.disabled}
                                style={{ background: btn.disabled ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 6, color: btn.disabled ? 'rgba(255,255,255,0.3)' : '#fff', padding: '6px 10px', cursor: btn.disabled ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center' }}>
                                {btn.icon}
                            </button>
                        ))}
                        <span style={{ color: '#fff', fontSize: 13, background: 'rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 8px', minWidth: 44, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
                    </div>
                    <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 6, color: '#fff', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}><X size={20} /></button>
                </div>
                <div onClick={e => e.stopPropagation()} onWheel={handleWheel} onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', overflow: 'hidden', cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default', userSelect: 'none', padding: '60px 80px', boxSizing: 'border-box' }}>
                    <div style={{ transform: `scale(${scale}) translate(${position.x / scale}px,${position.y / scale}px)`, transformOrigin: 'center center', transition: isDragging ? 'none' : 'transform 0.15s ease', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '100%', maxHeight: '100%' }}>
                        <SecureImage url={`${BASE_IMAGE_URL}images/pdi_report/${images[currentIndex]}`} token={userData?.accessToken} alt={`Image ${currentIndex + 1}`}
                            style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 140px)', objectFit: 'contain', borderRadius: 4, display: 'block', pointerEvents: 'none' }} />
                    </div>
                </div>
                {images.length > 1 && ['left', 'right'].map((side, i) => (
                    <button key={side} onClick={e => { e.stopPropagation(); i === 0 ? setCurrentIndex(p => (p - 1 + images.length) % images.length) : setCurrentIndex(p => (p + 1) % images.length); }}
                        style={{ position: 'absolute', [side]: '12px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', color: '#fff', width: 44, height: 44, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, zIndex: 10 }}>
                        {i === 0 ? '❮' : '❯'}
                    </button>
                ))}
            </div>
        );
    };

    // ─────────────────────────────────────────────
    // DETAIL VIEW (PDI items inside a report)
    // ─────────────────────────────────────────────
    const renderDetailView = () => {
        if (!selectedReport) return null;
        const filtered = filteredDetailItems;
        const paginated = paginatedDetailItems;

        return (
            <div className="report-detail-view-container">
                <div className="report-sticky-header">
                    {/* Info bar */}
                    <div className="report-approver-section" style={{padding: '10px 20px 10px 0'}}>
                        <div className="report-detail-info">
                            <span><strong>PO NO:</strong> {selectedReport.po_no}</span>
                            <span><strong>TPL NO:</strong> {selectedReport.tpl_no}</span>
                            <span><strong>FAB NO:</strong> {selectedReport.fab_no}</span>
                            <span><strong>Inspected By:</strong> {selectedReport.pdi_ckr_dts}</span>
                            <span><strong>Date Created:</strong> {formatDateTime(selectedReport.cd)}</span>
                            <span>
                                <strong>PDI Status:</strong>{' '}
                                <span className={getStatusClass(selectedReport.pdi_status)}>
                                    {getStatusDisplay(selectedReport.pdi_status)}
                                </span>
                            </span>
                        </div>
                        
                    </div>
                    <div className="report-approver-section" style={{padding: '10px 20px 10px 0'}}>
                        <div className="report-detail-info">
                            <span><strong>Remark:</strong> {selectedReport[`pdi_remark`] }</span>
                        </div>
                    </div>
                    {/* Search bar + Action button row */}
                    <div className="report-approver-section" style={{ flexDirection: 'row', justifyContent: 'right' }}>
                        <div className="report-controls">
                            <div className="report-search-box">
                                <Search size={16} />
                                <input type="text" placeholder="Search Part No / Desc / Size..." value={detailSearch}
                                    onChange={e => { setDetailSearch(e.target.value); setDetailPage(1); }} />
                            </div>
                            {detailSearch && (
                                <button className="rv-clear-btn"
                                    onClick={() => { setDetailSearch(''); setDetailPage(1); }}
                                    title="Clear search">
                                    <X size={15} />
                                </button>
                            )}
                        </div>
                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                            {(activeTab === 'dr_request' && canRqEdit) && (
                                <button
                                    className="dr-action-btn approve"
                                    onClick={() => setApproveTarget(selectedReport)}
                                    disabled={actionLoading}
                                    title="Approve this DR Request">
                                    <CheckCircle size={15} /> Approve DR Request
                                </button>
                            )}
                            {(activeTab === 'dr_approved' && canComEdit) && (
                                <button
                                    className="dr-action-btn complete"
                                    onClick={() => setCompleteTarget(selectedReport)}
                                    disabled={actionLoading}
                                    title="Mark as DR Completed">
                                    <CheckCircle size={15} /> Complete DR
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* PDI Table */}
                <div className="report-scrollable-container">
                    <div className="report-table-container">
                        <div className="report-sub-table-wrapper">
                            <table className="report-table">
                                <thead>
                                    <tr>{PDI_HEADERS.map((h, i) => <th key={i}>{h.label}</th>)}</tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr><td colSpan={PDI_HEADERS.length} style={{ textAlign: 'center', padding: '48px 20px', color: '#999' }}>No items found.</td></tr>
                                    ) : paginated.map(item => (
                                        <tr key={item._id || item.sn}>
                                            {PDI_HEADERS.map((header, hi) => (
                                                <td key={hi}>{item[header.key]}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Detail Pagination */}
                {filtered.length > 0 && (
                    <div className="report-pagination">
                        <div className="report-rows-per-page">
                            <span>Rows per page:</span>
                            <select value={detailPerPage} onChange={e => { setDetailPerPage(+e.target.value); setDetailPage(1); }}>
                                {[10, 15, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        <span>{Math.min((detailPage - 1) * detailPerPage + 1, filtered.length)}–{Math.min(detailPage * detailPerPage, filtered.length)} of {filtered.length}</span>
                        <div className="report-pagination-controls">
                            <button onClick={() => setDetailPage(1)} disabled={detailPage === 1}>⟪</button>
                            <button onClick={() => setDetailPage(p => p - 1)} disabled={detailPage === 1}>⟨</button>
                            <button onClick={() => setDetailPage(p => p + 1)} disabled={detailPage >= detailTotalPages}>⟩</button>
                            <button onClick={() => setDetailPage(detailTotalPages)} disabled={detailPage >= detailTotalPages}>⟫</button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ─────────────────────────────────────────────
    // MAIN RENDER
    // ─────────────────────────────────────────────
    return (
        <div className="report-container">
            <Breadcrumbs backNav={backNav} />

            {selectedReport ? renderDetailView() : (
                <>
                    {/* ── STICKY HEADER ───────────────────────── */}
                    <div className="report-sticky-header">

                        {/* ROW 1: Tabs + Search */}
                        <div className="report-header-content">
                            <div className="report-tabs">
                                {TABS.map(tab => (
                                    <button key={tab}
                                        className={`report-tab-btn ${activeTab === tab ? 'active' : ''} dr-tab-${tab.replace('_', '-')}`}
                                        onClick={() => handleTabChange(tab)}>
                                        {TAB_LABELS[tab]}
                                    </button>
                                ))}
                            </div>
                            <div className="report-controls">
                                <div className="report-search-box">
                                    <Search size={16} />
                                    <input type="text" placeholder="Search... (min 3 chars)" value={searchQuery}
                                        onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }} />
                                </div>
                                {searchQuery && (
                                    <button className="rv-clear-btn" onClick={() => { setSearchQuery(''); setCurrentPage(1); }} title="Clear search">
                                        <X size={15} />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* ROW 2: Filter bar */}
                        <div className="rv-filter-bar">
                            {/* Start Date — FIX 1: max = today, future blocked */}
                            <div className="rv-filter-group">
                                <label className="rv-filter-label">Start Date</label>
                                <input
                                    type="date"
                                    className="rv-date-input"
                                    value={stagedStartDate}
                                    max={stagedEndDate || todayStr}
                                    onChange={e => setStagedStartDate(e.target.value)}
                                />
                            </div>

                            <div className="rv-filter-sep">→</div>

                            {/* End Date — FIX 1: max = today, min = start date */}
                            <div className="rv-filter-group">
                                <label className="rv-filter-label">End Date</label>
                                <input
                                    type="date"
                                    className="rv-date-input"
                                    value={stagedEndDate}
                                    min={stagedStartDate || undefined}
                                    max={todayStr}
                                    onChange={e => setStagedEndDate(e.target.value)}
                                />
                            </div>

                            <div className="rv-filter-actions">
                                {/* FIX 4: Both start AND end date required to enable Filter button */}
                                <button
                                    className="rv-apply-btn"
                                    onClick={handleApplyFilters}
                                    disabled={loading || !hasStagedFilters}
                                    title={!hasStagedFilters ? 'Both start and end date are required' : ''}
                                >
                                    Filter
                                </button>

                                {/* FIX 2 & 3: Show Clear whenever staged OR applied filters exist */}
                                {(hasActiveFilters || stagedStartDate || stagedEndDate) && (
                                    <button className="rv-clear-filter-btn" onClick={handleClearFilters} title="Clear filters">
                                        <X size={13} /> Clear
                                    </button>
                                )}
                            </div>

                            {/* Export */}
                            <div style={{ marginLeft: 'auto' }}>
                                <button className="rv-export-all-btn"
                                    onClick={handleExportAllToExcel}
                                    disabled={exportingAll || loading || currentCount === 0}
                                    title="Export all to Excel">
                                    {exportingAll
                                        ? <><span className="rv-spinner" /> Exporting...</>
                                        : <><FileSpreadsheet size={15} /> Export Excel</>}
                                </button>
                            </div>
                        </div>

                        {/* Active filter chips */}
                        {hasActiveFilters && (
                            <div className="rv-active-filters">
                                <span className="rv-filter-chip-label">Active filters:</span>
                                {appliedStartDate && <span className="rv-filter-chip">From: {appliedStartDate}</span>}
                                {appliedEndDate && <span className="rv-filter-chip">To: {appliedEndDate}</span>}
                            </div>
                        )}
                    </div>

                    {/* ── TABLE ───────────────────────────────── */}
                    <div className="report-scrollable-container">
                        <div className="report-table-container">
                            <div className="report-table-wrapper">
                                <table className="report-table">
                                    <thead>
                                        <tr>
                                            <th>NO.</th>
                                            <th>TPL NO</th>
                                            <th>DATE CREATED</th>
                                            <th>PO NO</th>
                                            <th>FAB NO</th>
                                            <th>MODIFIED DATE</th>
                                            <th>PDI STATUS</th>
                                            <th>VIEW PDI</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentReports.length === 0 && !loading ? (
                                            <tr>
                                                <td colSpan={8}
                                                    style={{ textAlign: 'center', padding: '48px 20px', color: '#999' }}>
                                                    {hasActiveFilters ? 'No records match the current filters.' : 'No records found.'}
                                                </td>
                                            </tr>
                                        ) : currentReports.map((report, idx) => (
                                            <tr key={report._id}>
                                                <td>{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                                                <td>{report.tpl_no}</td>
                                                <td>{formatDateTime(report.cd)}</td>
                                                <td>{report.po_no}</td>
                                                <td>{report.fab_no}</td>
                                                <td>{formatDateTime(report.md)}</td>
                                                <td><span className={getStatusClass(report.pdi_status)}>{getStatusDisplay(report.pdi_status)}</span></td>
                                                <td>
                                                    {report.pdi?.length > 0 ? (
                                                        <button className="report-view-btn" title="View PDI items"
                                                            onClick={() => handleViewDetail(report)}>
                                                            <Eye size={18} />
                                                        </button>
                                                    ) : <span style={{ color: '#ccc', fontSize: 12 }}>—</span>}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── PAGINATION ───────────────────────────── */}
                    <div className="report-pagination">
                        <div className="report-rows-per-page">
                            <span>Rows per page:</span>
                            <select value={itemsPerPage} onChange={e => handlePageSizeChange(+e.target.value)}>
                                {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
                            </select>
                        </div>
                        {currentCount > 0 ? (
                            <span className="report-page-info">
                                Showing {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, currentCount)} of {currentCount} entries
                            </span>
                        ) : <span>Showing 0–0 of 0 entries</span>}
                        <div className="report-pagination-controls">
                            <button onClick={handleFirstPage} disabled={currentPage === 1 || loading}>⟪</button>
                            <button onClick={handlePreviousPage} disabled={currentPage === 1 || loading}>⟨</button>
                            {currentCount > 0
                                ? <span className="report-page-numbers">Page {currentPage} of {mainTotalPages}</span>
                                : <span>Page 0 of 0</span>}
                            <button onClick={handleNextPage} disabled={currentPage >= mainTotalPages || loading}>⟩</button>
                            <button onClick={handleLastPage} disabled={currentPage >= mainTotalPages || loading}>⟫</button>
                        </div>
                    </div>
                </>
            )}

            {/* ── MODALS ──────────────────────────────────── */}
            <ApproveModal
                report={approveTarget}
                onConfirm={handleApproveConfirm}
                onCancel={() => setApproveTarget(null)}
                loading={actionLoading}
            />
            <CompleteModal
                report={completeTarget}
                onConfirm={handleCompleteConfirm}
                onCancel={() => setCompleteTarget(null)}
                loading={actionLoading}
            />

            {/* ── IMAGE VIEWER ─────────────────────────────── */}
            <FullScreenImageViewer
                isOpen={fullScreenViewer.isOpen}
                onClose={handleCloseViewer}
                images={fullScreenViewer.images}
            />
        </div>
    );
};

export default DrReportView;